import { isPwaMode, TrackingEventTypeWEB } from '@capsicum/common';
import { ActivityIndicator, BaseQuantityInput, Button, Image, useIdParam, useRouter } from '@capsicum/common-web';
import { placeOrderPayloadAtom, setProductQuantityAtom, useProductPage } from '@capsicum/features/fanshop/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { styled, useTheme } from 'styled-components';

export type OrderSummaryProps = {
	params: { id?: string };
};

export const OrderSummary = ({ params }: OrderSummaryProps) => {
	const { colors } = useTheme();
	const router = useRouter();

	// Custom hooks
	const { id } = useIdParam(params, '/fan-shop/rewards');
	const { getTokenBalanceBy, isPending: isPendingNCWallet } = useNCWBalance();
	const {
		balanceAfterRedeem,
		eventDate,
		getCategory,
		getSsuAmount,
		hasEventDate,
		isBalanceBelowZero,
		isExperienceType,
		isFetching,
		isLoading,
		isPackageProduct,
		isPendingCWallet,
		isRedeemEnabled,
		isRedeemEnabledOrderSummary,
		isTicketType,
		mainBlurHash,
		mainImage,
		matchPlace,
		maxUserPurchase,
		packageQuantity,
		productQuantity,
		quantity,
		sendEventAnalytics,
		setQuantity,
		texts,
		totalPrice,
		variant
	} = useProductPage({ getTokenBalanceBy, variantId: id as string });

	const isPendingCombinedWallets = !isPendingCWallet && !isPendingNCWallet;

	const setPlaceOrderPayload = useSetAtom(placeOrderPayloadAtom);
	const setProductQuantity = useSetAtom(setProductQuantityAtom);

	const handleNavigation = useCallback(async () => {
		setQuantity(productQuantity);
		sendEventAnalytics(TrackingEventTypeWEB.track, {
			is_pwa_mode: isPwaMode(),
			page: 'Reward Summary'
		});
		router.push(`/fan-shop/rewards/order-details/${id}`);
	}, [id, productQuantity, router, sendEventAnalytics, setQuantity]);

	useEffect(() => {
		setPlaceOrderPayload({});
		if (isPackageProduct) {
			setProductQuantity(1);
		} else {
			setProductQuantity(0);
		}
	}, [setPlaceOrderPayload, setProductQuantity, isPackageProduct]);

	const imageSection = useMemo(
		() => (
			<S.ImageContainer>
				{mainImage ? (
					<S.StyledCardImage
						alt={`${variant?.label} image`}
						blurhash={mainBlurHash}
						height={303}
						optimized
						src={mainImage}
						width={256}
					/>
				) : (
					<S.StyledCardImagePlaceholder />
				)}
			</S.ImageContainer>
		),
		[mainBlurHash, mainImage, variant?.label]
	);

	const categoryInfoSection = useMemo(
		() => (
			<S.CategoryInfo>
				{(isExperienceType && hasEventDate) || isTicketType ? (
					<>
						<S.InfoWrapper>
							<S.DetailsTitle>{isExperienceType ? texts.eventDate : texts.matchDate}</S.DetailsTitle>
							<S.DetailsValue>{eventDate}</S.DetailsValue>
						</S.InfoWrapper>
						{isTicketType && (
							<div>
								<S.DetailsTitle>{texts.stadium}</S.DetailsTitle>
								<S.DetailsValue>{matchPlace}</S.DetailsValue>
							</div>
						)}
					</>
				) : (
					<div>
						<S.DetailsTitle>{texts.category}</S.DetailsTitle>
						<S.DetailsValue>{getCategory(variant)}</S.DetailsValue>
					</div>
				)}
			</S.CategoryInfo>
		),
		[getCategory, isTicketType, eventDate, matchPlace, texts.category, texts.matchDate, texts.stadium, variant]
	);

	const handleQuantityChange = useCallback(
		(value: number | string) => {
			const convertValue = Number(value);

			setQuantity(convertValue);
		},
		[setQuantity]
	);

	return (
		<S.Wrapper>
			<S.CategoryTitle>{texts.titleOrderSummary}</S.CategoryTitle>
			{isLoading && isFetching && isPendingCombinedWallets ? (
				<ActivityIndicator color={colors.icons.main.enabled} style={{ alignSelf: 'center' }} />
			) : (
				<S.OrderSummaryWrapper>
					{imageSection}
					<S.DetailsWrapper>
						<S.Category>{getCategory(variant)}</S.Category>
						<S.Title>{variant?.label}</S.Title>
						<S.ItemAmountWrapper>
							<S.TextSmall>{texts.itemAmount}</S.TextSmall>
							<S.ItemAmountQuantity data-testid={`order-summary-item-amount--${getSsuAmount()}`}>
								{`${getSsuAmount()} ${texts.rewardPoints}`}
							</S.ItemAmountQuantity>
						</S.ItemAmountWrapper>
						{categoryInfoSection}
						<S.TextsContainer>
							<S.DescriptionTitle>{texts.quantity}</S.DescriptionTitle>
							{isPackageProduct ? (
								<S.QuantityAmount>{packageQuantity}</S.QuantityAmount>
							) : (
								<BaseQuantityInput
									disabledAddition={
										quantity >= maxUserPurchase || quantity >= (variant?.availableStock || 0)
									}
									disabledSubstraction={quantity <= 0}
									max={Math.min(variant?.availableStock || 0, maxUserPurchase)}
									min={0}
									testID="order-summary-quantity-input"
									value={quantity}
									onChange={handleQuantityChange}
								/>
							)}
						</S.TextsContainer>
						<S.TextsContainer>
							<S.TotalTitle>{texts.total}</S.TotalTitle>
							<S.TotalText data-testid={`order-summary-total--${totalPrice}`}>
								{`${totalPrice} ${texts.rewardPoints}`}
							</S.TotalText>
						</S.TextsContainer>
						<S.TextsContainer>
							<S.BalanceAfterTitle>{texts.balanceAfter}</S.BalanceAfterTitle>
							<S.Description
								$notEnoughSSU={isBalanceBelowZero}
								data-testid={`order-summary-balance-after-redeem--${balanceAfterRedeem}`}
							>
								{`${balanceAfterRedeem} ${texts.rewardPoints}`}
							</S.Description>
						</S.TextsContainer>
						<S.ButtonWrapper>
							<Button
								disabled={!isRedeemEnabledOrderSummary || !isRedeemEnabled}
								fullWidth={isMobile}
								testID="order-summary-redeem-button"
								text={texts.redeemButton}
								onClick={handleNavigation}
							/>
						</S.ButtonWrapper>
					</S.DetailsWrapper>
				</S.OrderSummaryWrapper>
			)}
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		width: 100%;
		display: flex;
		flex-direction: column;
	`;

	export const CategoryTitle = styled.div`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: ${({ theme }) => theme.spacings.xxlarge} 0 ${({ theme }) => theme.spacings.large};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin-left: ${({ theme }) => theme.spacings.small};
		}
	`;

	export const OrderSummaryWrapper = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		width: 100%;
		display: grid;
		grid-template-columns: calc(50% - 10px) calc(50% - 10px);
		grid-column-gap: ${({ theme }) => theme.spacings.small};
		padding: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.border.radius.medium};
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			display: flex;
			flex-direction: column;
			margin-bottom: 70px;
		}
	`;

	export const DetailsWrapper = styled.div`
		display: flex;
		flex-direction: column;
		flex: 1;
	`;

	export const ImageContainer = styled.div`
		width: 100%;
		max-width: 100%;
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			margin-bottom: ${({ theme }) => theme.spacings.medium};
		}
	`;

	export const Category = styled.div`
		${({ theme }) => theme.fonts.text.caption1};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Title = styled.div`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ItemAmountWrapper = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		column-gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const TextSmall = styled.div`
		${({ theme }) => theme.fonts.text.text1}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ItemAmountQuantity = styled.div`
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const CategoryInfo = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
		border-top: 1px solid ${({ theme }) => theme.colors.borders.main.divider};
		display: flex;
	`;

	export const DetailsTitle = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.fonts.text.text1}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const InfoWrapper = styled.div`
		margin-right: ${({ theme }) => theme.spacings.medium};
	`;

	export const DetailsValue = styled.div`
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const TextsContainer = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
		border-top: 1px solid ${({ theme }) => theme.colors.borders.main.divider};
	`;

	export const DescriptionTitle = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.fonts.text.text1}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const QuantityAmount = styled.div`
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const BalanceAfterTitle = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.fonts.text.caption2}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const BalanceAfterText = styled.div`
		${({ theme }) => theme.fonts.text.text3}
	`;

	export const TotalTitle = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.fonts.text.text1}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Description = styled.div<{ $notEnoughSSU: boolean }>`
		${({ theme }) => theme.fonts.text.text3}
		white-space: pre-line;
		color: ${({ $notEnoughSSU, theme }) =>
			$notEnoughSSU ? theme.colors.text.error.enabled : theme.colors.text.main.enabled};
	`;

	export const TotalText = styled.div`
		${({ theme }) => theme.fonts.headings.title5}
		white-space: pre-line;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ButtonWrapper = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
		align-self: end;

		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			position: fixed;
			width: 100%;
			bottom: 0;
			left: 0;
			margin-top: 0;
			padding: ${({ theme }) => theme.spacings.small};
			background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
			display: flex;
			flex-direction: row;
			align-self: center;
		}
	`;

	export const StyledCardImage = styled(Image)`
		object-fit: contain;
		width: 100%;
		margin: 0 auto;
		${({ theme }) =>
			theme.breakpoints.queries.builder.or(
				theme.breakpoints.queries.above.mediumDesktop,
				theme.breakpoints.queries.only.mobile
			)} {
			width: 100%;
			max-width: 100%;
		}
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			width: 100%;
		}
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			width: 100%;
			max-width: 100%;
		}
	`;

	export const StyledCardImagePlaceholder = styled.div`
		height: 254px;
		width: 100%;
		background-color: ${({ theme }) => theme.colors.skeleton.default};
	`;
}
